import { error } from 'console';
import { noop } from './utils/common';
import { session_id } from './utils/session_id';
import { assertSudoWritable } from './utils/sudoState';

export const API = {
  fetch: async function (
    method: Request['method'],
    url: Request['url'],
    headers?: Record<string, string> | null | undefined,
    data?: any,
    signal?: any,
    onError: (response: Response) => void = noop,
    onResponse?: (response: Response) => void,
    // Client-side deadline for this call. Callers that can fail fast (e.g. an
    // interactive picker) pass a shorter one; everyone else keeps the 120s default.
    timeoutMs: number = 120_000,
  ) {
    // A tab whose sudo was switched elsewhere must not write into the other
    // tab's workspace. Throws SudoStaleError; reads are still allowed.
    assertSudoWritable(method);

    if (!headers) {
      headers = { 'Content-Type': 'application/json' };
    }

    const token = localStorage.getItem('token');

    if (token !== null) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    headers["X-Request-ID"] = session_id;

    const workspace_id = localStorage.getItem('workspace_id');

    if (workspace_id !== null) {
      headers['workspace-id'] = workspace_id;
    }

    const requestParameters: {
      method: string;
      headers: Record<string, string>;
      body?: any;
      signal?: any;
    } = {
      method: method,
      headers: headers,
    };

    const timeoutSignal = AbortSignal.timeout(timeoutMs);
    if (signal) {
      const combined = new AbortController();
      const abort = (reason: unknown) => combined.abort(reason);
      if (signal.aborted) {
        combined.abort(signal.reason);
      } else {
        signal.addEventListener('abort', () => abort(signal.reason), { once: true });
      }
      if (timeoutSignal.aborted) {
        combined.abort(timeoutSignal.reason);
      } else {
        timeoutSignal.addEventListener('abort', () => abort(timeoutSignal.reason), { once: true });
      }
      requestParameters['signal'] = combined.signal;
    } else {
      requestParameters['signal'] = timeoutSignal;
    }

    if (data) {
      requestParameters['body'] = JSON.stringify(data);
    }

    // Status and API message of a failed response, attached to the thrown error so a
    // caller can tell e.g. a 504 from a 422 without parsing the message text.
    let failedStatus: number | null = null;
    let failedDetail: string | null = null;

    try {
      const response = await fetch(url, requestParameters);

      if (!response.ok) {
        console.log('Failed to remove token. Got status: ', response.status);
      }

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('workspace_id');
        localStorage.removeItem('workspace_name');
        window.location.href = '/';
      }

      if (!response.ok) {
        let error_text = "";
        let error_text_raw = null;
        let error_parameters = null;
        let error_detail: any = null;

        try {
          const data = await response.json();
          console.log("Error", data)
          error_detail = data?.detail ?? null;
          if (data?.error) {
            error_text = data?.error;
          } else if (data?.detail && Array.isArray(data.detail) && data.detail.length > 0) {
            error_text = data?.detail[0]?.msg;
          } else if (data?.detail && !Array.isArray(data.detail)) {
            error_text = data?.detail;
          }
          error_text_raw = data?.error_message_raw;
          error_parameters = data?.error_parameters || null;
        } catch {
          console.log("Failed to parse error response");
        }

        failedStatus = response.status;
        failedDetail = error_text || null;

        if (response.status === 404) {
          throw new Error('Client error: ' + (error_text ?? 'Page not found...'));
        }
        if (response.status === 412) {
          if (error_text_raw && error_parameters) {
            throw new Error(error_text_raw, {
              cause: {
                status: response.status,
                parameters: error_parameters,
              }
            });
          } else {
            throw new Error('Client error: ' + (error_text ?? 'Precondition failed'));
          }
        }
        if (response.status === 401) {
          throw new Error('Client error: ' + (error_text ?? 'Authentication error'));
        }

        if (response.status === 422) {
          throw new Error('Client error: ' + (error_text ?? 'Validation error'));
        }
        if (response.status === 409) {
          throw new Error('Conflict error', {
            cause: {
              status: response.status,
              detail: error_detail,
            },
          });
        }
        if (response.status >= 400 && response.status < 500) {
          if (response.status === 402 && error_text === 'No search quota available') {
            throw new Error(error_text, { cause: response.status });
          } else {
            throw new Error('Client error: ' + (error_text ?? 'Client error'));
          }
        }

        if (response.status === 500) {
          throw new Error('Server error: Internal server error');
        }

        throw new Error(error_text ?? 'Something went wrong...');
      }

      if (response.status === 204) {
        return
      }

      onResponse?.(response);

      return await response.json();
    } catch (error) {
      if (failedStatus !== null && error instanceof Error) {
        Object.assign(error, { status: failedStatus, responseDetail: failedDetail });
      }
      throw error;
    }
  },

  async fetchBlob(
    method: Request['method'],
    url: Request['url'],
    headers?: Record<string, string>,
    data?: any,
  ) {
    assertSudoWritable(method);

    if (!headers) {
      headers = { 'Content-Type': 'application/json' };
    }

    let token = localStorage.getItem('token');
    if (token !== null) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const workspace_id = localStorage.getItem('workspace_id');
    if (workspace_id !== null) {
      headers['workspace-id'] = workspace_id;
    }

    const requestParameters: {
      method: string;
      headers: Record<string, string>;
      body?: any;
    } = {
      method: method,
      headers: headers,
    };

    if (data) {
      requestParameters['body'] = JSON.stringify(data);
    }

    try {
      const response = await fetch(url, requestParameters);

      if (!response.ok) {
        console.log('Failed to remove token. Got status: ', response.status);
      }

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('workspace_id');
        localStorage.removeItem('workspace_name');
        window.location.href = '/';
      }

      if (!response.ok) {
        return {};
      }

      return await response.blob();
    } catch (error) {
      return false;
    }
  },

  async Fetch(
    method: Request['method'],
    url: Request['url'],
    headers?: Record<string, string>,
    data?: any,
  ) {
    assertSudoWritable(method);

    if (!headers) {
      headers = { 'Content-Type': 'application/json' };
    }

    headers["X-Request-ID"] = session_id;

    let token = localStorage.getItem('token');
    if (token !== null) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const workspace_id = localStorage.getItem('workspace_id');
    if (workspace_id !== null) {
      headers['workspace-id'] = workspace_id;
    }

    const requestParameters: {
      method: string;
      headers: Record<string, string>;
      body?: any;
    } = {
      method: method,
      headers: headers,
    };

    if (data) {
      requestParameters['body'] = JSON.stringify(data);
    }

    try {
      const response = await fetch(url, requestParameters);

      if (!response.ok) {
        console.log('Failed to remove token. Got status: ', response.status);
      }

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('workspace_id');
        localStorage.removeItem('workspace_name');
        window.location.href = '/';
      }

      if (response.status === 422) {
        let newError = await response.json();
        let errorMessage = newError?.detail[0]?.msg;
        throw new Error(errorMessage);
      }

      // if (response.status >= 400) {
      //   throw new Error('Validation error');
      // }

      return response;
    } catch (error) {
      console.log(error);
      return false;
    }
  },
};

export const ShareAPI = {
  async fetch(
    method: Request['method'],
    url: Request['url'],
    headers?: Record<string, string>,
    data?: any,
    signal?: any,
    onError: (response: Response) => void = noop,
  ) {
    if (!headers) {
      headers = { 'Content-Type': 'application/json' };
    }

    let token = localStorage.getItem('share_token');
    if (token !== null) {
      headers['Share-Authorization'] = `Bearer ${token}`;
    }

    const requestParameters: {
      method: string;
      headers: Record<string, string>;
      body?: any;
      signal?: any;
    } = {
      method: method,
      headers: headers,
    };

    const timeoutSignal = AbortSignal.timeout(120_000);
    if (signal) {
      const combined = new AbortController();
      const abort = (reason: unknown) => combined.abort(reason);
      if (signal.aborted) {
        combined.abort(signal.reason);
      } else {
        signal.addEventListener('abort', () => abort(signal.reason), { once: true });
      }
      if (timeoutSignal.aborted) {
        combined.abort(timeoutSignal.reason);
      } else {
        timeoutSignal.addEventListener('abort', () => abort(timeoutSignal.reason), { once: true });
      }
      requestParameters['signal'] = combined.signal;
    } else {
      requestParameters['signal'] = timeoutSignal;
    }

    if (data) {
      requestParameters['body'] = JSON.stringify(data);
    }

    try {
      const response = await fetch(url, requestParameters);

      if (!response.ok) {
        console.log('Failed to remove token. Got status: ', response.status);

        if (response.status === 404) {
          throw new Error('Page not found...')
        }
  
        if (response.status === 400) {
          throw new Error('Page not found...')
        }
      }

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('share_token');
        onError(response);
      }

      if (!response.ok) {
        onError(response);
        return {};
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },
};
